import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { client } from '../../../api/NestClient';
import type { paths } from '../../../api/types/api';
import { RTK_CACHE_LIFETIME } from '../../../config';
import { nestBaseQuery } from '../../../utils/nestBaseQuery';

export type Gstr1SummaryResponse =
  paths['/api/v1/gstr1/summary']['get']['responses']['200']['content']['application/json'];

export type Gstr1DateRangeQueryArgs = {
  fromDate: number;
  toDate: number;
};

type NestClientGetResult = Awaited<ReturnType<typeof client.GET>>;

const gstr1NestBaseQuery: BaseQueryFn<
  NestClientGetResult | Promise<NestClientGetResult>,
  unknown,
  { message: string; status: number; originalResponse: unknown }
> = async (clientCall) => nestBaseQuery(await clientCall);

export const gstr1Api = createApi({
  reducerPath: 'gstr1Api',
  baseQuery: gstr1NestBaseQuery,
  tagTypes: ['Gstr1Summary'],
  endpoints: (builder) => ({
    getGstr1Summary: builder.query<Gstr1SummaryResponse | null, Gstr1DateRangeQueryArgs>({
      query: ({ fromDate, toDate }) =>
        client.GET('/api/v1/gstr1/summary', {
          params: { query: { fromDate, toDate } },
        }),
      transformResponse: (response: Gstr1SummaryResponse | undefined) => response ?? null,
      providesTags: (_result, _error, { fromDate, toDate }) => [
        { type: 'Gstr1Summary', id: `${fromDate}-${toDate}` },
      ],
      keepUnusedDataFor: RTK_CACHE_LIFETIME,
    }),
  }),
});

export const { useGetGstr1SummaryQuery, useLazyGetGstr1SummaryQuery } = gstr1Api;

/** Call after sales/POS/purchase/credit/debit vouchers change so GSTR-1 summary refetches. */
export const invalidateGstr1SummaryCache = (dispatch: (action: unknown) => unknown) => {
  dispatch(gstr1Api.util.invalidateTags(['Gstr1Summary']));
};
